import { GraphQLClient } from 'graphql-request'
import { Subject } from 'rxjs'
import { render } from 'solid-js/web'
import { Sdk, getSdk } from '../../__generated__/sdk.generated'
import { fetchDeveloperApp, fetchListingByUrl } from '../../queries'
import { logger } from '../../util/logger'
import { EmbeddedFrame, EmbeddedFrameCommand } from '../frame'
import * as OkOfferButton from '../trigger/ok-offer-button'
import { OfferKitTrigger } from '../trigger/trigger'
import { Mount, useMount } from './mount'

// Define event types used in OfferKit
export type FrameEventName =
  | 'open'
  | 'close'
  | 'buyer-registered'
  | 'offer-created'

// Base interface for all frame events
export interface FrameEvent {
  type: FrameEventName
  timestamp: number
  data?: Record<string, any>
}

const GRAPHQL_ENDPOINT = import.meta.env.VITE_GRAPHQL_ENDPOINT!
const FRAMES_HOST = import.meta.env.VITE_FRAMES_HOST!
const FRAMES_DEMO_EVENT_HOST = import.meta.env.VITE_FRAMES_DEMO_EVENT_HOST!

type QueuedCall = {
  a: [string, any]
  rs: (result?: any) => void
  rj: (err: any) => void
}

type EventCallback = (event: FrameEvent) => void

declare global {
  interface Window {
    OfferKit?: OfferKitStub
  }
}

interface OfferKitStub {
  q: QueuedCall[]
  exec: (name: string, args?: any) => Promise<any>
  __is_offerkit__?: boolean
}

export class OfferKitEmbeddable implements OfferKitStub {
  readonly q = []
  readonly __is_offerkit__ = true

  appId: string | null = null
  internalMode: boolean = false
  initialized: boolean = false
  rootHost: HTMLElement | null = null
  frameHost: HTMLElement | null = null
  triggerHost: HTMLElement | null = null
  __demoEventMode = false
  channel = new Subject<EmbeddedFrameCommand>()
  events = new Subject<FrameEvent>()
  triggers: Set<OfferKitTrigger> = new Set()
  disposeFrame: (() => void) | undefined
  sdk: Sdk
  mount: Mount
  private eventSubscriptions: Map<string, Set<EventCallback>> = new Map()

  static install() {
    if (
      window.OfferKit &&
      typeof window.OfferKit === 'object' &&
      window.OfferKit.__is_offerkit__
    ) {
      console.error(
        '[OfferKit] OfferKit was loaded more than once on the same page. ' +
          'Ensure you only include one copy of the OfferKit loader script on your page.\n' +
          new Error().stack
      )

      return false
    }

    const ok = new OfferKitEmbeddable()
    const queue = window.OfferKit?.q
    if (queue) {
      ok.flush(queue)
    }

    window.OfferKit = ok
    logger.log('installed OfferKit.')
    return true
  }

  static uninstall() {
    window.OfferKit?.exec('uninstall', {})
  }

  constructor() {
    this.sdk = getSdk(
      new GraphQLClient(GRAPHQL_ENDPOINT, {
        headers: {
          'apollographql-client-name': 'Script',
        },
      })
    )
    this.mount = useMount({ kit: this, sdk: this.sdk })

    // Set up event subscription for internal events
    this.events.subscribe((event) => {
      const callbacks = this.eventSubscriptions.get(event.type) || new Set()
      callbacks.forEach((callback) => {
        try {
          callback(event)
        } catch (error) {
          logger.error('Error in event callback:', error)
        }
      })
    })
  }

  async exec(name: string, args: any) {
    switch (name) {
      case 'init':
        await this.init(args)
        break
      case 'mount':
        return await this.mount(args)
      case 'unmount':
        this.unmount()
        break
      case 'uninstall':
        this.uninstall()
        break
      case 'on':
        return this.on(args.event, args.callback)
      case 'off':
        return this.off(args.event, args.callback)
      default:
        throw new Error(`Unknown method ${name}.`)
    }
  }

  async flush(queue: QueuedCall[]) {
    logger.log('flushing queue:', queue)
    while (queue.length) {
      const { rs, rj, a } = queue.shift()!
      const args = [...a]
      await this.exec(args[0] as string, args[1]).then(rs, rj)
    }
  }

  async initializeDeveloperApp(appId: string) {
    this.appId = appId
    const app = await fetchDeveloperApp(this.sdk, this.appId)

    if (app?.automount) {
      const listing = await fetchListingByUrl(
        this.sdk,
        this.appId,
        window.location.href
      )

      if (listing) {
        await this.mount({ listingId: listing.source.foreignId })
      }
    }
  }

  async initializeInternalMode(internalMode?: boolean) {
    this.internalMode = Boolean(internalMode)
  }

  async renderElements() {
    if (!this.rootHost) {
      this.rootHost = document.createElement('div')
      this.rootHost.setAttribute('id', 'OfferKit_host')
      document.body.appendChild(this.rootHost)

      this.triggerHost = document.createElement('div')
      this.triggerHost.setAttribute('id', 'OfferKit_button-host')
      this.rootHost.appendChild(this.triggerHost)

      this.frameHost = document.createElement('div')
      this.frameHost.setAttribute('id', 'OfferKit_frame-host')
      this.frameHost.setAttribute('role', 'dialog')
      this.frameHost.setAttribute('aria-modal', 'true')
      this.frameHost.setAttribute('aria-label', 'Buyer Hub')
      this.rootHost.appendChild(this.frameHost)
    }
    this.render()
  }

  async init({
    appId,
    internalMode,
    __demoEventMode,
  }:
    | {
        internalMode: boolean
        appId?: never
        __demoEventMode?: boolean
      }
    | {
        internalMode?: never
        appId: string
        __demoEventMode?: boolean
      }): Promise<void> {
    if (!appId && !internalMode) {
      throw new Error('You must supply the `appId` parameter to `init`.')
    }

    this.initialized = true
    this.__demoEventMode = Boolean(__demoEventMode)
    await this.renderElements()

    if (appId) {
      return this.initializeDeveloperApp(appId)
    }

    await this.initializeInternalMode(internalMode)

    OkOfferButton.install()
  }

  render() {
    const origin =
      this.__demoEventMode && FRAMES_DEMO_EVENT_HOST
        ? FRAMES_DEMO_EVENT_HOST
        : FRAMES_HOST
    this.disposeFrame = render(
      () => (
        <EmbeddedFrame
          origin={origin}
          appId={this.appId!}
          commands={this.channel}
          onEvent={this.handleFrameEvent.bind(this)}
        />
      ),
      this.frameHost!
    )
  }

  /**
   * Add an event listener for a specific frame event
   *
   * @param eventName The event to listen for ('open', 'close', 'buyer-registered', 'offer-created')
   * @param callback The function to call when the event occurs
   * @returns An unsubscribe function that can be called to remove the listener
   */
  on(eventName: FrameEventName, callback: EventCallback): () => void {
    if (!this.eventSubscriptions.has(eventName)) {
      this.eventSubscriptions.set(eventName, new Set())
    }
    const callbacks = this.eventSubscriptions.get(eventName)!
    callbacks.add(callback)

    // Return an unsubscribe function
    return () => {
      callbacks.delete(callback)
      if (callbacks.size === 0) {
        this.eventSubscriptions.delete(eventName)
      }
    }
  }

  /**
   * Remove an event listener
   *
   * @param eventName The event to stop listening for
   * @param callback The callback function to remove
   */
  off(eventName: FrameEventName, callback: EventCallback): void {
    const callbacks = this.eventSubscriptions.get(eventName)
    if (callbacks) {
      callbacks.delete(callback)
      if (callbacks.size === 0) {
        this.eventSubscriptions.delete(eventName)
      }
    }
  }

  // Map internal event type to cleaner external event type
  private mapEventType(internalType: string): FrameEventName | null {
    switch (internalType) {
      case 'frame-event:opened':
        return 'open'
      case 'frame-event:closed':
        return 'close'
      case 'frame-event:buyer-registered':
        return 'buyer-registered'
      case 'frame-event:offer-created':
        return 'offer-created'
      default:
        return null
    }
  }

  // Validate and normalize the event
  private normalizeEvent(event: any): FrameEvent | null {
    if (
      !event ||
      typeof event !== 'object' ||
      !('type' in event) ||
      typeof event.type !== 'string'
    ) {
      return null
    }

    const eventType = this.mapEventType(event.type)
    if (!eventType) {
      return null
    }

    const timestamp = event.timestamp || Date.now()

    // Create a standardized event object
    const normalizedEvent: FrameEvent = {
      type: eventType,
      timestamp,
    }

    // Add specific data based on event type
    switch (eventType) {
      case 'buyer-registered':
        if (typeof event.buyerId === 'string') {
          normalizedEvent.data = event.buyer
        } else {
          return null // Invalid event, missing required data
        }
        break

      case 'offer-created':
        if (typeof event.offerId === 'string') {
          normalizedEvent.data = event.offer
        } else {
          return null // Invalid event, missing required data
        }
        break
    }

    return normalizedEvent
  }

  handleFrameEvent(event: any) {
    const normalizedEvent = this.normalizeEvent(event)
    if (normalizedEvent) {
      logger.log('Received frame event:', normalizedEvent)
      this.events.next(normalizedEvent)
    } else {
      logger.warn('Received invalid frame event:', event)
    }
  }

  unmount() {
    for (const trigger of this.triggers) {
      trigger.unmount()
    }
    this.triggers = new Set()
    logger.log('unmounted all triggers.')
  }

  uninstall() {
    this.unmount()
    if (this.frameHost) {
      this.disposeFrame?.()
      document.body.removeChild(this.frameHost)
      logger.log('uninstalled OfferKit from frame host.')
    }

    // Clear all event subscriptions
    this.eventSubscriptions.clear()

    if (window.OfferKit === this) {
      delete window.OfferKit
      logger.log('deleted OfferKit from window.')
    }
  }

  enableDebugMode() {
    logger.setDebugMode(true)
    logger.warn('debug mode has been enabled.')
  }
}
